import { useState } from 'react';
import { setCookie } from 'utils/cookie';
import useUser from 'store/user/useUser';

import {
  Box,
  Dialog,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Grid,
  useTheme,
} from '@mui/material';

import { ReactComponent as Number01 } from 'assets/icon-number-01.svg';
import { ReactComponent as Number02 } from 'assets/icon-number-02.svg';
import { ReactComponent as Number03 } from 'assets/icon-number-03.svg';
import { ReactComponent as Number04 } from 'assets/icon-number-04.svg';
import { ReactComponent as Number05 } from 'assets/icon-number-05.svg';

export interface SimpleDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setShow: (show: boolean) => void;
}

const welcomeTitleSx = {
  fontWeight: 700,
  fontSize: { xs: 28, sm: 40, md: 56, lg: 64 },
  lineHeight: { xs: 1.25, md: 1.25 },
} as const;

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: { xs: 22, sm: 28, md: 36, lg: 40 },
  lineHeight: { xs: 1.3, md: 1.25 },
} as const;

function Introduction(props: SimpleDialogProps) {
  const { palette } = useTheme();
  const { open, setOpen, setShow } = props;
  const { student_name } = useUser();

  const contents = [
    {
      title: 'Platform',
      desc1: 'Smart ENR is an electronic nursing record system designed',
      desc2: 'for nursing students and new nurses',
      desc3: '',
    },
    {
      title: 'Our Solution',
      desc1: 'We developed Smart ENR to help nursing students and new',
      desc2: 'nurses overcome these challenges by practicing with an EHR',
      desc3: 'system',
    },
    {
      title: 'Background',
      desc1: 'Approximately 97% of hospitals in the US employ electronic',
      desc2: 'health record (EHR) systems, and nurses spend up to 35% of',
      desc3: 'their working hours on nursing documentation',
    },
    {
      title: 'Usability',
      desc1: 'Smart ENR is cloud-based, allowing you to access it anytime',
      desc2: 'anywhere, and on any device—PC, laptop, tablet, or',
      desc3: 'smartphone—without the need for installation',
    },
    {
      title: 'Current Problem',
      desc1: 'Most new nurses face challenges with nursing records',
      desc2: '',
      desc3: '',
    },
  ];

  const icons = [
    <Number01 fill={palette.primary.main} />,
    <Number04 fill={palette.primary.main} />,
    <Number02 fill={palette.primary.main} />,
    <Number05 fill={palette.primary.main} />,
    <Number03 fill={palette.primary.main} />,
  ];

  const [check, setCheck] = useState(false);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      scroll="paper"
      onClose={() => setOpen(false)}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: 'calc(100% - 24px)', sm: 'min(90%, 960px)', lg: '75%' },
          maxHeight: { xs: '92vh', md: 'none' },
          m: { xs: 1.5, sm: 2 },
          p: { xs: 2.5, sm: 4, md: 5, lg: '50px 100px' },
          backgroundColor: palette.mode === 'dark' ? 'grey.800' : undefined,
          boxSizing: 'border-box',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        alignItems={{ xs: 'flex-start', sm: 'baseline' }}
        columnGap={0.5}
      >
        <Typography sx={welcomeTitleSx}>Welcome,</Typography>
        <Typography sx={{ ...welcomeTitleSx, color: palette.primary.main }}>
          {student_name}
        </Typography>
      </Stack>
      <Typography sx={{ ...welcomeTitleSx, mt: { xs: 0.5, md: 0 } }}>
        This is Smart ENR :)
      </Typography>
      <Typography
        sx={{
          color: palette.primary.main,
          fontWeight: 700,
          fontSize: { xs: 18, sm: 22, md: 26, lg: 30 },
          lineHeight: { xs: 1.4, md: 1.5 },
          mt: { xs: 1.5, md: 2 },
        }}
      >
        Shall we go practice nursing records?
      </Typography>
      <Grid
        container
        columnSpacing={{ xs: 0, sm: 4, md: 10 }}
        rowSpacing={{ xs: 3, sm: 4, md: 5 }}
        sx={{ mt: { xs: 3, sm: 4, md: 7.75 }, mb: { xs: 3, md: 5.5 } }}
      >
        {contents.map(({ title, desc1, desc2, desc3 }, idx) => (
          <Grid item xs={12} md={6} key={idx}>
            <Stack
              direction="row"
              spacing={{ xs: 1.5, sm: 2 }}
              alignItems="center"
              sx={{ mb: { xs: 1.5, md: 2.5 } }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  '& svg': {
                    width: { xs: 36, sm: 44, md: 52 },
                    height: 'auto',
                  },
                }}
              >
                {icons[idx]}
              </Box>
              <Typography sx={{ ...sectionTitleSx, color: palette.primary.main }}>
                {title}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: 14, sm: 15, md: 16 },
                lineHeight: 1.6,
              }}
            >
              {desc1}
              {desc2 ? (
                <>
                  <br />
                  {desc2}
                </>
              ) : null}
              {desc3 ? (
                <>
                  <br />
                  {desc3}
                </>
              ) : null}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Button
          sx={{
            width: { xs: '100%', sm: 187 },
            height: 48,
            color: palette.primary.main,
            flexShrink: 0,
          }}
          variant="outlined"
          onClick={() => {
            if (check) setCookie('no_intro', 'true', 30);
            setShow(false);
            setOpen(false);
          }}
        >
          Tap to Close
        </Button>
        <FormControlLabel
          sx={{
            m: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: { xs: 14, sm: 16 },
            },
          }}
          control={
            <Checkbox
              checked={check}
              onChange={e => setCheck(e.target.checked)}
            />
          }
          label="Do not open"
        />
      </Stack>
    </Dialog>
  );
}

export default Introduction;
